import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import api from '../api';

const fmtCurrency = (v) => v != null ? `$${Number(v).toFixed(2)}` : '$0.00';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

export default function POPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getPurchaseOrder(id);
        setOrder(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!order) return <div className="p-8 text-center text-red-500">Purchase Order not found</div>;

  return (
    <div data-testid="po-print-page">
      <div className="print:hidden bg-card border-b px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-muted" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-sm text-muted-foreground flex-1">Purchase Order Preview — {order.po_number}</span>
        <button onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
          data-testid="print-po-btn">
          <Printer className="h-4 w-4" /> Print PO
        </button>
      </div>

      <div className="max-w-[800px] mx-auto bg-white p-8 my-6 print:my-0 print:p-6 print:shadow-none shadow-lg" style={{color: '#111'}}>
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-start gap-3">
            <img src="/logo.jpg" alt="Webb Supply" className="h-14 w-14 rounded-md object-cover" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{color: '#111'}}>Webb Supply</h1>
              <div className="text-sm mt-1" style={{color: '#444'}}>
                <div>227 County Road 125 East</div>
                <div>Norris City, IL 62869</div>
                <div>618-962-3211</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold tracking-tight" style={{color: '#111'}}>PURCHASE ORDER</h2>
            <div className="text-sm mt-2" style={{color: '#444'}}>
              <div><strong>PO #:</strong> {order.po_number}</div>
              <div><strong>Date:</strong> {fmtDate(order.order_date || order.created_at)}</div>
              {order.expected_date && <div><strong>Expected:</strong> {fmtDate(order.expected_date)}</div>}
              {order.payment_terms && <div><strong>Terms:</strong> {order.payment_terms}</div>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-6 text-sm">
          <div style={{marginTop: '0.25in', minHeight: '1in'}}>
            <div className="font-semibold text-xs uppercase tracking-wider mb-2" style={{color: '#666'}}>Vendor</div>
            <div className="font-semibold text-base" style={{color: '#111'}}>{order.vendor_name || '—'}</div>
            {order.vendor_address && <div>{order.vendor_address}</div>}
            {(order.vendor_city || order.vendor_state || order.vendor_zip) && (
              <div>{[order.vendor_city, order.vendor_state, order.vendor_zip].filter(Boolean).join(', ')}</div>
            )}
            {order.vendor_contact && <div>{order.vendor_contact}</div>}
            {order.vendor_phone && <div>{order.vendor_phone}</div>}
            {order.vendor_email && <div>{order.vendor_email}</div>}
            {order.vendor_fax && <div>Fax: {order.vendor_fax}</div>}
          </div>
          <div>
            <div className="font-semibold text-xs uppercase tracking-wider mb-2" style={{color: '#666'}}>Ship To</div>
            <div className="font-semibold">Webb Supply</div>
            <div>227 County Road 125 East</div>
            <div>Norris City, IL 62869</div>
          </div>
        </div>

        {order.location && (
          <div className="mb-6 text-xs bg-gray-100 rounded px-4 py-2" style={{color: '#444'}}>
            <strong>Location:</strong> {order.location}
          </div>
        )}

        <table className="w-full mb-6 text-sm" style={{borderCollapse: 'collapse'}}>
          <thead>
            <tr style={{borderBottom: '2px solid #333'}}>
              <th className="text-left py-2 font-semibold" style={{color: '#111'}}>Item</th>
              <th className="text-left py-2 font-semibold" style={{color: '#111'}}>SKU</th>
              <th className="text-center py-2 font-semibold" style={{color: '#111'}}>Qty</th>
              <th className="text-right py-2 font-semibold" style={{color: '#111'}}>Unit Cost</th>
              <th className="text-right py-2 font-semibold" style={{color: '#111'}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(order.line_items || []).map((item, i) => (
              <tr key={i} style={{borderBottom: '1px solid #ddd'}}>
                <td className="py-2" style={{color: '#222'}}>{item.variant_name || item.description || '—'}</td>
                <td className="py-2 text-xs" style={{color: '#666'}}>{item.sku || ''}</td>
                <td className="py-2 text-center">{item.quantity}</td>
                <td className="py-2 text-right">{fmtCurrency(item.unit_price || item.unit_cost)}</td>
                <td className="py-2 text-right font-medium">{fmtCurrency(item.total || item.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 text-sm">
            <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
              <span style={{color: '#666'}}>Subtotal</span>
              <span className="font-medium">{fmtCurrency(order.subtotal)}</span>
            </div>
            {order.tax > 0 && (
              <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
                <span style={{color: '#666'}}>Tax</span>
                <span className="font-medium">{fmtCurrency(order.tax)}</span>
              </div>
            )}
            {order.freight > 0 && (
              <div className="flex justify-between py-1" style={{borderBottom: '1px solid #ddd'}}>
                <span style={{color: '#666'}}>Freight</span>
                <span className="font-medium">{fmtCurrency(order.freight)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 font-bold text-base" style={{borderTop: '2px solid #333'}}>
              <span>Total</span>
              <span>{fmtCurrency(order.total)}</span>
            </div>
          </div>
        </div>

        {order.remarks && (
          <div className="mt-6 pt-4 border-t text-sm" style={{color: '#666'}}>
            <strong>Remarks:</strong> {order.remarks}
          </div>
        )}

        <div className="mt-8 pt-4 border-t text-center text-xs" style={{color: '#999'}}>
          Webb Supply — Purchase Order
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; background: white; }
          .print\\:hidden { display: none !important; }
          .print\\:my-0 { margin-top: 0 !important; margin-bottom: 0 !important; }
          .print\\:p-6 { padding: 1.5rem !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
